import React, { useEffect, useMemo, useState } from "react";
import { Copy, GitMerge, Search, Layers } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

// Surfaces auto-detected duplicate groups + a manual picker to merge any same-type entities.
export const DuplicatesPanel = ({ groups, nodeTypeEntries, onMerge }) => {
  const [manualType, setManualType] = useState(nodeTypeEntries?.[0]?.key || "");
  const [manualNodes, setManualNodes] = useState([]);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(new Set());

  useEffect(() => {
    if (!manualType) return;
    setPicked(new Set());
    api.get(`/kg/nodes?type=${manualType}&limit=0`).then((res) => setManualNodes(res.data)).catch(() => setManualNodes([]));
  }, [manualType]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return manualNodes
      .filter((n) => n.status !== "archived")
      .filter((n) => !q || (n.label || "").toLowerCase().includes(q));
  }, [manualNodes, query]);

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const startManualMerge = () => {
    const chosen = manualNodes.filter((n) => picked.has(n.id))
      .map((n) => ({ id: n.id, label: n.label, status: n.status, created_by: n.created_by, data: n.data }));
    onMerge(manualType, chosen);
  };

  return (
    <div data-testid="duplicates-panel">
      <h2 className="font-serif text-2xl text-foreground mb-1">Merge duplicates</h2>
      <p className="text-sm text-muted-foreground mb-5">
        Entities that look like the same thing. Merging keeps one survivor and moves all relationships, notes and progress onto it. Every merge is reviewed by a second curator or admin.
      </p>

      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
          <Copy className="w-4 h-4 text-primary" /> Auto-detected ({groups?.length || 0})
        </div>
        {(!groups || groups.length === 0) ? (
          <div className="bg-card border border-border border-dashed rounded-lg p-8 text-center text-muted-foreground" data-testid="duplicates-empty">
            No same-name duplicates found.
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => (
              <div key={`${g.type}-${g.name_fold}`} className="bg-card border border-border rounded-lg p-4" data-testid={`dup-group-${g.type}-${g.name_fold}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <span className="font-medium text-foreground">{g.members[0].label}</span>
                    <Badge variant="outline" className="ml-2 border-accent/40 text-accent">{g.type_label}</Badge>
                    <span className="text-xs text-muted-foreground ml-2">{g.members.length} copies</span>
                  </div>
                  <Button size="sm" onClick={() => onMerge(g.type, g.members)} className="bg-primary hover:bg-primary/90"
                    data-testid={`dup-merge-${g.type}-${g.name_fold}`}>
                    <GitMerge className="w-4 h-4 mr-1" /> Merge these
                  </Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {g.members.map((m) => (
                    <Badge key={m.id} variant="outline" className="text-[10px] font-normal">
                      {m.status} · {m.created_by || "—"}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
          <Layers className="w-4 h-4 text-primary" /> Merge manually
        </div>
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Select value={manualType} onValueChange={setManualType}>
              <SelectTrigger className="w-[220px]" data-testid="manual-merge-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {nodeTypeEntries.map((nt) => <SelectItem key={nt.key} value={nt.key}>{nt.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by name…"
                className="pl-8" data-testid="manual-merge-search" />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto border border-border rounded-md divide-y divide-border">
            {filtered.map((n) => (
              <label key={n.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-secondary/40"
                data-testid={`manual-merge-node-${n.id}`}>
                <Checkbox checked={picked.has(n.id)} onCheckedChange={() => toggle(n.id)} />
                <span className="text-foreground">{n.label}</span>
                <Badge variant="outline" className="text-[10px] ml-auto">{n.status}</Badge>
              </label>
            ))}
            {filtered.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">No entities.</div>}
          </div>
          <Button size="sm" disabled={picked.size < 2} onClick={startManualMerge} className="bg-primary hover:bg-primary/90"
            data-testid="manual-merge-btn">
            <GitMerge className="w-4 h-4 mr-1" /> Merge {picked.size} selected
          </Button>
        </div>
      </div>
    </div>
  );
};
